import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Moon, Sparkles, Star, Crown, CheckCircle, Loader2, AlertCircle, CreditCard } from 'lucide-react';
import api from '../lib/api';
import Navigation from '../components/Navigation';

const PLANS = [
  { id: 'day_pass', name: 'One Day Pass', price_inr: '\u20B969', price_usd: '$1', desc: "For Today's Questions - Unlimited", icon: Moon },
  { id: 'monthly', name: 'Monthly Pass', price_inr: '\u20B9299', price_usd: '$3.99', desc: 'Unlimited readings, all month', icon: Sparkles, recommended: true },
  { id: 'quarterly', name: '3 Months Pass', price_inr: '\u20B9699', price_usd: '$7.99', desc: 'Three months of Deeper Clarity', icon: Star },
  { id: 'yearly', name: '1 Year Pass', price_inr: '\u20B92,999', price_usd: '$32.99', desc: 'Your cards all year long', icon: Crown },
];

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) { resolve(true); return; }
    const script = document.createElement('script');
    script.id = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function PaywallPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('plans');
  const [expiryMsg, setExpiryMsg] = useState('');
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [payTab, setPayTab] = useState({});

  useEffect(() => { checkAccess(); }, []);

  const checkAccess = async () => {
    try {
      const { data } = await api.get('/tarot/check-access');
      if (data.has_access) { navigate('/home'); return; }
      if (data.expiry_message) setExpiryMsg(data.expiry_message);
    } catch (e) { console.error('Access check failed:', e); }
  };

  const getPayTab = (planId) => payTab[planId] || 'card';
  const setPayTabFor = (planId, tab) => setPayTab(prev => ({ ...prev, [planId]: tab }));

  const handleStripeCheckout = async (planId) => {
    setLoadingPlan(planId);
    try {
      const { data } = await api.post('/payments/create-checkout', {
        plan_id: planId,
        origin_url: window.location.origin
      });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleRazorpayCheckout = useCallback(async (planId) => {
    setLoadingPlan(planId);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { alert('Razorpay failed to load. Please try again.'); setLoadingPlan(null); return; }

      const { data } = await api.post('/payments/razorpay/create-order', { plan_id: planId });

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: 'Tarot Room',
        description: PLANS.find(p => p.id === planId)?.name || 'Subscription',
        prefill: { name: user?.username || '', email: user?.email || '' },
        theme: { color: '#D4AF37' },
        handler: async (response) => {
          try {
            const verifyRes = await api.post('/payments/razorpay/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: planId
            });
            if (verifyRes.data.verified) {
              setPhase('success');
              if (refreshUser) await refreshUser();
              setTimeout(() => navigate('/home'), 2500);
            }
          } catch {
            alert('Payment verification failed. Please contact support.');
          }
        },
        modal: { ondismiss: () => setLoadingPlan(null) }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      alert(e.response?.data?.detail || 'Payment initialization failed.');
    } finally {
      setLoadingPlan(null);
    }
  }, [user, navigate, refreshUser]);

  return (
    <div className="min-h-screen bg-[#070A1A] relative" data-testid="paywall-page">
      <Navigation />
      <div className="absolute inset-0 mystic-gradient" />

      <div className="relative z-10 px-4 md:px-12 lg:px-24 py-12 pb-24 md:pb-12 max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="w-24 h-36 mx-auto mb-8 rounded-xl bg-gradient-to-br from-[#1D1129] to-[#0F0B1E] border-2 border-[#D4AF37]/30 flex items-center justify-center animate-float">
            <span className="text-3xl text-[#D4AF37]">?</span>
          </div>
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#FFFFF0]" data-testid="paywall-title">
            {expiryMsg ? 'Time to Recharge' : 'The cards have more to reveal...'}
          </h1>
          {expiryMsg && (
            <div className="flex items-center justify-center gap-2 mt-4 p-3 rounded-xl bg-[#C87886]/10 border border-[#C87886]/20 max-w-lg mx-auto">
              <AlertCircle size={16} className="text-[#C87886] shrink-0" />
              <p className="text-sm text-[#C87886]" data-testid="expiry-message">{expiryMsg}</p>
            </div>
          )}
          {!expiryMsg && (
            <p className="text-sm text-[#BDB2C4] mt-4 max-w-lg mx-auto leading-relaxed">
              You've received your 2 complimentary readings. To continue your journey — and unlock unlimited guidance — choose your path.
            </p>
          )}
        </motion.div>

        <AnimatePresence>
          {phase === 'success' && (
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              className="glass-card p-8 mb-8 border-[#D4AF37]/40 text-center" style={{ background: 'rgba(212,175,55,0.05)' }}
              data-testid="payment-verified">
              <CheckCircle size={48} className="text-[#D4AF37] mx-auto mb-4" />
              <h3 className="font-['Playfair_Display'] text-xl text-[#FFFFF0] mb-2">Pass Activated!</h3>
              <p className="text-sm text-[#D4AF37]">Welcome to unlimited guidance.</p>
              <p className="text-xs text-[#BDB2C4] mt-2">Redirecting to your reading...</p>
            </motion.div>
          )}
        </AnimatePresence>

        {phase !== 'success' && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {PLANS.map((plan, idx) => {
                const Icon = plan.icon;
                const currentTab = getPayTab(plan.id);
                const isLoading = loadingPlan === plan.id;
                return (
                  <motion.div key={plan.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 * idx }}
                    data-testid={`plan-${plan.id}`}
                    className={`glass-card p-6 transition-all duration-300 ${plan.recommended ? 'border-[#D4AF37]/40 shadow-[0_0_30px_rgba(212,175,55,0.1)]' : ''}`}>
                    {plan.recommended && (
                      <span className="text-[10px] text-[#D4AF37] tracking-widest uppercase bg-[#D4AF37]/10 px-3 py-1 rounded-full mb-3 inline-block">Recommended</span>
                    )}
                    <div className="flex items-center gap-3 mb-3">
                      <Icon size={20} className="text-[#D4AF37]" strokeWidth={1.5} />
                      <h3 className="font-['Playfair_Display'] text-lg text-[#FFFFF0]">{plan.name}</h3>
                    </div>
                    <p className="text-sm text-[#BDB2C4] mb-4">{plan.desc}</p>
                    <div className="flex items-baseline gap-2 mb-4">
                      <span className="text-xl font-['Playfair_Display'] text-[#D4AF37]">{plan.price_usd}</span>
                      <span className="text-xs text-[#BDB2C4]">/ {plan.price_inr}</span>
                    </div>

                    {/* Payment Tabs */}
                    <div className="flex rounded-lg overflow-hidden border border-white/10 mb-3" data-testid={`pay-tabs-${plan.id}`}>
                      <button onClick={() => setPayTabFor(plan.id, 'card')}
                        className={`flex-1 py-2 text-xs font-medium transition-all ${currentTab === 'card' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#BDB2C4] hover:text-[#FFFFF0]'}`}
                        data-testid={`tab-card-${plan.id}`}>
                        Pay in USD
                      </button>
                      <button onClick={() => setPayTabFor(plan.id, 'upi')}
                        className={`flex-1 py-2 text-xs font-medium transition-all ${currentTab === 'upi' ? 'bg-[#D4AF37]/10 text-[#D4AF37] border-b-2 border-[#D4AF37]' : 'text-[#BDB2C4] hover:text-[#FFFFF0]'}`}
                        data-testid={`tab-upi-${plan.id}`}>
                        Pay in INR
                      </button>
                    </div>

                    {currentTab === 'card' ? (
                      <button onClick={() => handleStripeCheckout(plan.id)} disabled={isLoading}
                        className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid={`checkout-card-${plan.id}`}>
                        {isLoading ? <><Loader2 size={14} className="animate-spin" /> Processing...</> : <><CreditCard size={14} /> Pay {plan.price_usd}</>}
                      </button>
                    ) : (
                      <button onClick={() => handleRazorpayCheckout(plan.id)} disabled={isLoading}
                        className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid={`checkout-upi-${plan.id}`}>
                        {isLoading ? <><Loader2 size={14} className="animate-spin" /> Processing...</> : <>Pay {plan.price_inr}</>}
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </div>

            <p className="text-center text-sm text-[#BDB2C4]/60 font-['Playfair_Display'] italic">
              No judgment. No pressure. The cards will wait — but your answers won't.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
